import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { AuditLog } from '../../models/audit-log';
import { PageMeta } from '../../models/page-meta';
import { AuditLogsService } from '../../services/audit-logs/audit-logs';

@Component({
  selector: 'app-audit-logs',
  standalone: false,
  templateUrl: './audit-logs.html',
  styleUrls: ['./audit-logs.css']
})
export class AuditLogs implements OnInit {
  logs: AuditLog[] = [];

  loading = false;
  search = '';
  entityName = '';
  entityId = '';
  adminUserId = '';

  page = 1;
  size = 10;

  meta: PageMeta = {
    page: 1,
    size: 10,
    totalItems: 0,
    totalPages: 0
  };

  constructor(
    private auditLogsService: AuditLogsService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  loadLogs(): void {
    this.loading = true;

    this.auditLogsService
      .getLogs(
        this.page,
        this.size,
        this.entityName,
        this.entityId,
        this.adminUserId,
        this.search
      )
      .pipe(finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: response => {
          if (!response.success || !Array.isArray(response.data)) {
            this.logs = [];
            this.meta = this.createMeta(0);
            this.toastr.error(response.message || 'Unable to load audit logs.');
            this.cdr.detectChanges();
            return;
          }

          this.logs = response.data;
          this.meta = response.meta || this.createMeta(this.logs.length);
          this.cdr.detectChanges();
        },
        error: error => {
          this.logs = [];
          this.meta = this.createMeta(0);
          this.toastr.error(
            error?.error?.message ||
              error?.error?.error?.details ||
              'Unable to load audit logs.',
            'Audit Logs'
          );
          this.cdr.detectChanges();
        }
      });
  }

  applyFilters(): void {
    this.page = 1;
    this.loadLogs();
  }

  clearFilters(): void {
    this.search = '';
    this.entityName = '';
    this.entityId = '';
    this.adminUserId = '';
    this.page = 1;
    this.loadLogs();
  }

  private createMeta(totalItems: number): PageMeta {
    return {
      page: this.page,
      size: this.size,
      totalItems,
      totalPages: Math.max(1, Math.ceil(totalItems / this.size))
    };
  }

  previousPage(): void {
    if (this.page <= 1) return;

    this.page--;
    this.loadLogs();
  }

  nextPage(): void {
    if (this.page >= this.meta.totalPages) return;

    this.page++;
    this.loadLogs();
  }

  getActionLabel(actionType: number): string {
    switch (actionType) {
      case 1:
        return 'Create';
      case 2:
        return 'Update';
      case 3:
        return 'Activate';
      case 4:
        return 'Disable';
      case 5:
        return 'Suspend';
      case 6:
        return 'Revoke';
      case 7:
        return 'Renew';
      case 8:
        return 'Login';
      case 9:
        return 'Register';
      case 10:
        return 'Role Change';
      default:
        return 'Unknown';
    }
  }

  getActionClass(actionType: number): string {
    switch (actionType) {
      case 1:
      case 3:
      case 7:
      case 8:
      case 9:
        return 'text-bg-success';
      case 4:
      case 5:
      case 6:
        return 'text-bg-danger';
      case 10:
        return 'text-bg-warning';
      default:
        return 'text-bg-secondary';
    }
  }
}
